import React, { useState, useRef, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { chatService, llmConfigService, processService, projectService, resolveGoogleDocsAuthUrl } from '../../services/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ChatHistorySidebar from './ChatHistorySidebar';
import CodeAnalysisForm from './CodeAnalysisForm';
import ProcessSelector from '../ProcessSelector';
import GoogleDocsExportModal from '../GoogleDocsExportModal';

const ChatTab = () => {
  const { projectId } = useParams();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [currentModel, setCurrentModel] = useState(null);
  const [showAnalysisForm, setShowAnalysisForm] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [exportingMessageId, setExportingMessageId] = useState(null);
  const [processes, setProcesses] = useState([]);
  const [selectedProcessId, setSelectedProcessId] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportedDocument, setExportedDocument] = useState({ url: '', title: '' });
  const [isLoadingProcesses, setIsLoadingProcesses] = useState(false);
  const [projectName, setProjectName] = useState(null);
  const [showClearHistoryModal, setShowClearHistoryModal] = useState(false);
  const [showGoogleDocsPermissionModal, setShowGoogleDocsPermissionModal] = useState(false);
  const [googleDocsAuthUrl, setGoogleDocsAuthUrl] = useState('');
  const chatContainerRef = useRef(null);

  // Load conversations and processes on mount or when projectId changes
  useEffect(() => {
    if (projectId) {
      // Reset state when switching projects
      setCurrentConversationId(null);
      setMessages([]);
      setError(null);
      setMetadata(null);
      setSelectedProcessId(null);
      
      loadConversations();
      loadModelSettings();
      loadProcesses();
      loadProjectName();
    } else {
      setIsLoadingConversations(false);
      setIsLoading(false);
    }
  }, [projectId]);

  // Listen for process changes from Project Configuration tab
  useEffect(() => {
    const handleProcessesChanged = (event) => {
      // Only reload if the event is for the current project
      if (event.detail.projectId === projectId) {
        console.log('🔄 Processes changed, reloading...');
        loadProcesses();
      }
    };

    window.addEventListener('processesChanged', handleProcessesChanged);

    return () => {
      window.removeEventListener('processesChanged', handleProcessesChanged);
    };
  }, [projectId]);

  // Load project name
  const loadProjectName = async () => {
    if (!projectId) return;
    
    try {
      const response = await projectService.getProject(projectId);
      if (response.data && response.data.name) {
        setProjectName(response.data.name);
      }
    } catch (err) {
      console.error('Error loading project name:', err);
      // Don't show error to user, just log it
    }
  };

  // Load processes for the project
  const loadProcesses = async () => {
    if (!projectId) return;
    
    setIsLoadingProcesses(true);
    try {
      const response = await processService.getProcessesByProject(projectId);
      const processesData = response.data || [];
      console.log('📋 Loaded processes:', processesData.length, processesData.map(p => ({ id: p.id, name: p.name })));
      setProcesses(processesData);
      
      // If a process was selected but no longer exists, clear selection
      if (selectedProcessId && !processesData.find(p => p.id === selectedProcessId)) {
        console.log('⚠️ Selected process no longer exists, clearing selection');
        setSelectedProcessId(null);
      }
      
      // If there's only one process left and it was selected, clear selection
      if (processesData.length <= 1 && selectedProcessId) {
        console.log('⚠️ Only 1 or fewer processes, clearing selection');
        setSelectedProcessId(null);
      }
    } catch (err) {
      console.error('Error loading processes:', err);
      setProcesses([]);
    } finally {
      setIsLoadingProcesses(false);
    }
  };

  // Handle process selection change
  const handleProcessChange = (newProcessId) => {
    console.log('🔄 Process selection changed:', { from: selectedProcessId, to: newProcessId });
    setSelectedProcessId(newProcessId);
    // Optionally clear current conversation when changing scope
    // setCurrentConversationId(null);
    // setMessages([]);
  };

  // Load conversations list
  const loadConversations = async () => {
    setIsLoadingConversations(true);
    try {
      const response = await chatService.getConversations(projectId, false);
      const convs = response.data || [];
      setConversations(convs);
      
      // Always show new chat state when switching projects
      setMessages([]);
      setCurrentConversationId(null);
      setIsLoading(false);
    } catch (err) {
      console.error('Error loading conversations:', err);
      setIsLoading(false);
    } finally {
      setIsLoadingConversations(false);
    }
  };

  // Load a specific conversation
  const loadConversation = async (conversationId) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await chatService.getConversationMessages(conversationId);
      setMessages(response.data || []);
      setCurrentConversationId(conversationId);
    } catch (err) {
      console.error('Error loading conversation:', err);
      setError('Failed to load conversation');
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Create a new conversation
  const handleNewChat = async () => {
    setCurrentConversationId(null);
    setMessages([]);
    setMetadata(null);
    setError(null);
  };

  // Load available models and current preference
  const loadModelSettings = async () => {
    try {
      const [modelsRes, preferenceRes] = await Promise.all([
        llmConfigService.getAvailableModels(),
        llmConfigService.getPreference(projectId)
      ]);
      
      setAvailableModels(modelsRes.data || []);
      setCurrentModel(preferenceRes.data || null);
    } catch (err) {
      console.error('Error loading model settings:', err);
      // Set defaults on error - non-critical feature
      setCurrentModel({
        modelName: 'gemini-2.5-pro',
        temperature: 0.2,
        isDefault: true
      });
      setAvailableModels([]);
    }
  };

  // Auto-scroll to bottom when new messages are added, typing indicator shows, or loading finishes
  useEffect(() => {
    if (chatContainerRef.current) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [messages, isTyping, isLoading]);

  const loadChatHistory = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await chatService.getChatHistory(projectId, false);
      
      if (response.data && response.data.length > 0) {
        setMessages(response.data);
      } else {
        // No history, show welcome message
        setMessages([
          { id: 1, message: "Hello! I'm your UiPath code assistant. Ask me anything about your project's workflows, activities, or structure!", sender: 'assistant', timestamp: new Date().toISOString() }
        ]);
      }
    } catch (err) {
      console.error('Error loading chat history:', err);
      setError('Failed to load chat history');
      setMessages([
        { id: 1, message: "Hello! I'm your UiPath code assistant. Ask me anything about your project!", sender: 'assistant', timestamp: new Date().toISOString() }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete a conversation
  const handleDeleteConversation = async (conversationId) => {
    try {
      await chatService.deleteConversation(conversationId);
      
      // Remove from list
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      
      // If we deleted the current conversation, create a new one
      if (conversationId === currentConversationId) {
        handleNewChat();
      }
    } catch (err) {
      console.error('Error deleting conversation:', err);
      setError('Failed to delete conversation');
    }
  };

  const sendMessage = async () => {
    const message = inputMessage.trim();
    if (!message || isTyping) return;

    // Clear input and error immediately
    setInputMessage('');
    setError(null);
    setIsTyping(true);
    
    // Reset textarea height to default
    const textarea = document.querySelector('textarea[placeholder*="Ask about"]');
    if (textarea) {
      textarea.style.height = 'auto';
    }

    // Add user message optimistically
    const tempUserMsg = {
      id: `temp-user-${Date.now()}`,
      message,
      sender: 'user',
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      // Call RAG-powered chat API with conversationId and processId
      console.log('📤 Sending message with:', { projectId, conversationId: currentConversationId, processId: selectedProcessId });
      const response = await chatService.sendMessage(projectId, message, currentConversationId, false, selectedProcessId);
      
      // If this was a new conversation, update the conversationId
      if (!currentConversationId && response.data.conversationId) {
        setCurrentConversationId(response.data.conversationId);
        // Add the new conversation to the list (don't reload to avoid resetting state)
        setConversations(prev => [{
          id: response.data.conversationId,
          projectId,
          title: message.length > 50 ? message.substring(0, 47) + '...' : message,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isCrossProject: false,
          _count: { messages: 2 }
        }, ...prev]);
      }
      
      // Replace temp message with actual server response
      setMessages(prev => {
        // Remove temp message and add both user and assistant messages from server
        const withoutTemp = prev.filter(m => m.id !== tempUserMsg.id);
        return [...withoutTemp, response.data.userMessage, response.data.assistantMessage];
      });

      // Store metadata for display (optional)
      if (response.data.metadata) {
        setMetadata(response.data.metadata);
      }
      
      // Update the conversation in the list (update timestamp)
      if (response.data.conversationId) {
        setConversations(prev => 
          prev.map(c => 
            c.id === response.data.conversationId 
              ? { ...c, updatedAt: new Date().toISOString(), _count: { messages: (c._count?.messages || 0) + 2 } }
              : c
          )
        );
      }
    } catch (err) {
      console.error('Error sending message:', err);
      
      // Determine user-friendly error message
      let userErrorMessage = 'Sorry, I encountered an error. Please try again.';
      
      if (err.response?.status === 503) {
        userErrorMessage = 'The AI service is temporarily unavailable. Please try again in a moment.';
      } else if (err.response?.status === 504) {
        userErrorMessage = 'The request timed out. Please try a simpler question or try again later.';
      } else if (err.response?.status === 500) {
        userErrorMessage = 'An internal server error occurred. Our team has been notified. Please try again later.';
      } else if (err.response?.data?.error) {
        userErrorMessage = err.response.data.error;
        if (err.response.data.details) {
          userErrorMessage += `\n\n${err.response.data.details}`;
        }
      } else if (err.message?.includes('Network Error')) {
        userErrorMessage = 'Network connection lost. Please check your internet connection and try again.';
      }
      
      // Show error message in chat
      const errorMsg = {
        id: `error-${Date.now()}`,
        message: `❌ **Error**: ${userErrorMessage}`,
        sender: 'assistant',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMsg]);
      setError(`Failed to send message: ${err.response?.data?.error || err.message}`);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    // Plain Enter sends the message
    if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
    // Ctrl+Enter or Shift+Enter creates a new line (allow default behavior)
  };

  const handleAnalyzeCodeClick = () => {
    // Start a new chat for code analysis
    handleNewChat();
    setShowAnalysisForm(true);
  };

  const handleCodeAnalysis = async (analysisRequest) => {
    setIsAnalyzing(true);
    setIsTyping(true); // Show typing indicator during analysis
    setShowAnalysisForm(false);
    setError(null);

    try {
      // Add user message about analysis request
      const analysisDescription = `Running code analysis for ${
        analysisRequest.scope === 'full-project' ? 'full project' :
        analysisRequest.scope === 'full-process' ? 'selected process' :
        `${analysisRequest.files.length} selected file(s)`
      }...`;

      const tempUserMsg = {
        id: `temp-user-${Date.now()}`,
        message: analysisDescription,
        sender: 'user',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, tempUserMsg]);

      // Call analysis API
      const response = await chatService.analyzeCode(
        analysisRequest.projectId,
        analysisRequest.processId,
        analysisRequest.scope,
        analysisRequest.files,
        currentConversationId
      );

      // Update conversation ID if this was a new conversation
      if (!currentConversationId && response.data.conversationId) {
        setCurrentConversationId(response.data.conversationId);
        // Add the new conversation to the list (don't reload to avoid resetting state)
        const title = `Code Analysis: ${
          analysisRequest.scope === 'full-project' ? 'Full Project' :
          analysisRequest.scope === 'full-process' ? 'Process' :
          `${analysisRequest.files.length} File(s)`
        }`;
        
        setConversations(prev => [{
          id: response.data.conversationId,
          projectId,
          title,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isCrossProject: false,
          _count: { messages: 2 }
        }, ...prev]);
      }

      // Replace temp message with actual server response
      setMessages(prev => {
        const withoutTemp = prev.filter(m => m.id !== tempUserMsg.id);
        return [...withoutTemp, response.data.userMessage, response.data.assistantMessage];
      });

      // Store metadata
      if (response.data.metadata) {
        setMetadata(response.data.metadata);
      }

      // Update conversation list
      if (response.data.conversationId) {
        setConversations(prev =>
          prev.map(c =>
            c.id === response.data.conversationId
              ? { ...c, updatedAt: new Date().toISOString(), _count: { messages: (c._count?.messages || 0) + 2 } }
              : c
          )
        );
      }
      
      // Reload processes in case new processes were created during analysis
      loadProcesses();
    } catch (err) {
      console.error('Error running code analysis:', err);

      const errorMsg = {
        id: `error-${Date.now()}`,
        message: err.response?.data?.error || 'Code analysis failed. Please try again.',
        sender: 'assistant',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMsg]);
      setError('Code analysis failed');
    } finally {
      setIsAnalyzing(false);
      setIsTyping(false); // Hide typing indicator
    }
  };

  const clearHistory = async () => {
    setShowClearHistoryModal(true);
  };

  const handleConfirmClearHistory = async () => {
    try {
      await chatService.clearChatHistory(projectId, false);
      setConversations([]);
      setMessages([]);
      setCurrentConversationId(null);
      setMetadata(null);
      setError(null);
      setShowClearHistoryModal(false);
    } catch (err) {
      console.error('Error clearing history:', err);
      setError('Failed to clear chat history');
      setShowClearHistoryModal(false);
    }
  };

  const handleCancelClearHistory = () => {
    setShowClearHistoryModal(false);
  };

  const handleModelChange = async (modelName) => {
    try {
      await llmConfigService.setPreference(modelName, currentModel?.temperature || 0.2, projectId);
      const preferenceRes = await llmConfigService.getPreference(projectId);
      setCurrentModel(preferenceRes.data);
    } catch (err) {
      console.error('Error changing model:', err);
      setError('Failed to change model');
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Strip markdown formatting from text
  const stripMarkdown = (text) => {
    let cleaned = text;
    
    // Remove bold (**text** or __text__)
    cleaned = cleaned.replace(/\*\*([^*]+?)\*\*/g, '$1');
    cleaned = cleaned.replace(/__([^_]+?)__/g, '$1');
    
    // Remove italic (*text* or _text_)
    cleaned = cleaned.replace(/\*([^*]+?)\*/g, '$1');
    cleaned = cleaned.replace(/_([^_]+?)_/g, '$1');
    
    // Remove code blocks (```code```)
    cleaned = cleaned.replace(/```[\s\S]*?```/g, (match) => {
      // Extract code content without the backticks and language identifier
      return match.replace(/```\w*\n?/g, '').replace(/```/g, '');
    });
    
    // Remove inline code (`code`)
    cleaned = cleaned.replace(/`([^`]+)`/g, '$1');
    
    // Remove headers (# ## ###)
    cleaned = cleaned.replace(/^#{1,6}\s+/gm, '');
    
    // Remove strikethrough (~~text~~)
    cleaned = cleaned.replace(/~~([^~]+?)~~/g, '$1');
    
    // Remove links but keep text [text](url) -> text
    cleaned = cleaned.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    
    // Remove images ![alt](url) -> alt
    cleaned = cleaned.replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1');
    
    // Remove horizontal rules (---, ___, ***)
    cleaned = cleaned.replace(/^[\-_*]{3,}\s*$/gm, '');
    
    // Remove blockquotes (>)
    cleaned = cleaned.replace(/^>\s+/gm, '');
    
    // Clean up extra whitespace
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
    
    return cleaned.trim();
  };

  // Copy message to clipboard
  const handleCopyMessage = async (messageId, content) => {
    try {
      // Strip markdown formatting before copying
      const plainText = stripMarkdown(content);
      await navigator.clipboard.writeText(plainText);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      setError('Failed to copy message');
    }
  };

  // Export message to Google Docs
  const handleExportToGoogleDocs = async (messageId, content) => {
    try {
      setExportingMessageId(messageId);
      const response = await chatService.exportMessageToGoogleDocs(messageId, content, projectName);
      
      if (response.data && response.data.documentUrl) {
        const { documentUrl, title } = response.data;
        setExportedDocument({ url: documentUrl, title });
        setShowExportModal(true);
      }
    } catch (err) {
      console.error('Error exporting to Google Docs:', err);
      
      // Check if we need to request Google Docs permissions
      if (err.response?.data?.needsAuth) {
        const currentPath = location.pathname;
        const authUrl = resolveGoogleDocsAuthUrl(currentPath);
        setGoogleDocsAuthUrl(authUrl);
        setShowGoogleDocsPermissionModal(true);
        return;
      }
      
      // Handle other errors
      let errorMessage = 'Failed to export to Google Docs. ';
      if (err.response?.status === 401) {
        errorMessage += 'Please log in with Google to use this feature.';
      } else if (err.response?.data?.error) {
        errorMessage += err.response.data.error;
      }
      alert(errorMessage);
    } finally {
      setExportingMessageId(null);
    }
  };

  const handleConfirmGoogleDocsPermission = () => {
    window.location.href = googleDocsAuthUrl;
  };

  const handleCancelGoogleDocsPermission = () => {
    setShowGoogleDocsPermissionModal(false);
    setGoogleDocsAuthUrl('');
  };

  const suggestedQuestions = [
    "What does this project do?",
    "Show me the login workflow",
    "Are there any error handling issues?",
    "What Excel activities are being used?",
    "Explain the Main workflow structure"
  ];

  // Custom components for ReactMarkdown
  const markdownComponents = {
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';
      
      return !inline && language ? (
        <SyntaxHighlighter
          style={vscDarkPlus}
          language={language}
          PreTag="div"
          className="rounded-lg my-2"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }
  };

  return (
    <div className="flex gap-0 h-full w-full overflow-hidden">
      {/* Main Chat Area - Full width */}
      <div className="flex-1 flex flex-col rounded-none border-0 border-r border-gray-200 bg-white overflow-hidden px-4 pt-4 pb-4 relative">
      
        {/* Loading Overlay - Only on chat area */}
        {isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10">
            <div className="text-gray-500">Loading...</div>
          </div>
        )}
      
        {/* Scrollable Chat Messages Area */}
        <div ref={chatContainerRef} className="flex-1 overflow-y-auto mb-4">
          <div
            className="flex flex-col space-y-3 px-2"
          >
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-lg px-4 py-3 ${
                  message.sender === 'user'
                    ? 'bg-blue-500 text-white'
                    : message.isError
                    ? 'bg-red-100 text-red-800 border border-red-300'
                    : 'bg-white text-gray-800 border border-gray-200'
                }`}
              >
                {message.sender === 'assistant' && !message.isError ? (
                  <div className="chat-markdown">
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      components={markdownComponents}
                    >
                      {message.message || message.text}
                    </ReactMarkdown>
                    
                    {/* Display LLM Execution Metrics and Action Buttons */}
                    {message.llmExecution && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-center justify-between gap-4">
                          {/* Left side: Metrics */}
                          <div className="flex items-center gap-3 text-xs text-gray-600">
                            {message.timestamp && (
                              <span className="text-gray-500">
                                {formatTimestamp(message.timestamp)}
                              </span>
                            )}
                            <span className="font-medium text-gray-700">
                              🤖 {message.llmExecution.displayName}
                            </span>
                            <span>
                              📊 {message.llmExecution.inputTokens.toLocaleString()} in / {message.llmExecution.outputTokens.toLocaleString()} out
                            </span>
                            <span className="text-blue-600">
                              ⏱️ {(message.llmExecution.durationMs / 1000).toFixed(2)}s
                            </span>
                            <span className="font-medium text-green-700">
                              💰 ${Math.ceil(parseFloat(message.llmExecution.estimatedCost) * 1000) / 1000}
                              {(() => {
                                // Calculate cumulative cost up to and including this message
                                const messageIndex = messages.findIndex(m => m.id === message.id);
                                const cumulativeCost = messages
                                  .slice(0, messageIndex + 1)
                                  .filter(m => m.llmExecution && m.llmExecution.estimatedCost)
                                  .reduce((sum, m) => sum + parseFloat(m.llmExecution.estimatedCost), 0);
                                return ` (total: $${cumulativeCost.toFixed(3)})`;
                              })()}
                            </span>
                          </div>
                          
                          {/* Right side: Action buttons */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleCopyMessage(message.id, message.message || message.text)}
                              className="text-gray-400 hover:text-blue-600 transition-colors p-1.5 rounded hover:bg-blue-50"
                              title={copiedMessageId === message.id ? 'Copied!' : 'Copy message'}
                            >
                              {copiedMessageId === message.id ? (
                                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              )}
                            </button>
                            <button
                              onClick={() => handleExportToGoogleDocs(message.id, message.message || message.text)}
                              disabled={exportingMessageId === message.id}
                              className="text-gray-400 hover:text-blue-500 transition-colors p-1.5 rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Export to Google Docs"
                            >
                              {exportingMessageId === message.id ? (
                                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" fill="#4285F4"/>
                                  <path d="M14 2v6h6" fill="#A1C2FA"/>
                                  <path d="M7 13h10v1H7zm0 2h10v1H7zm0 2h7v1H7z" fill="white"/>
                                </svg>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Action Icons for messages without metrics */}
                    {!message.llmExecution && (
                      <div className="mt-3 pt-2 border-t border-gray-200 flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleCopyMessage(message.id, message.message || message.text)}
                          className="text-gray-400 hover:text-blue-600 transition-colors p-1.5 rounded hover:bg-blue-50"
                          title={copiedMessageId === message.id ? 'Copied!' : 'Copy message'}
                        >
                          {copiedMessageId === message.id ? (
                            <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => handleExportToGoogleDocs(message.id, message.message || message.text)}
                          disabled={exportingMessageId === message.id}
                          className="text-gray-400 hover:text-blue-500 transition-colors p-1.5 rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Export to Google Docs"
                        >
                          {exportingMessageId === message.id ? (
                            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" fill="#4285F4"/>
                              <path d="M14 2v6h6" fill="#A1C2FA"/>
                              <path d="M7 13h10v1H7zm0 2h10v1H7zm0 2h7v1H7z" fill="white"/>
                            </svg>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm whitespace-pre-wrap break-words">
                    {message.message || message.text}
                  </div>
                )}
                {message.timestamp && !message.llmExecution && (
                  <div className={`text-xs mt-2 ${
                    message.sender === 'user' ? 'text-blue-100' : 'text-gray-500'
                  }`}>
                    {formatTimestamp(message.timestamp)}
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-white text-gray-800 border border-gray-200 rounded-lg px-4 py-3">
                <div className="flex space-x-2">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fixed Bottom Section */}
      <div className="flex-shrink-0">
        {/* Suggested Questions (show when no messages, not typing, not analyzing, and not showing analysis form) */}
        {messages.length <= 1 && !isTyping && !isAnalyzing && !showAnalysisForm && (
          <div className="mb-4 px-2">
            <p className="text-sm text-gray-600 mb-2">Try asking:</p>
            <div className="flex flex-wrap gap-2">
              {suggestedQuestions.map((question, idx) => (
                <button
                  key={idx}
                  onClick={() => setInputMessage(question)}
                  className="text-xs bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded-full hover:bg-gray-50 transition-colors"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Metadata Display (optional) */}
        {metadata && metadata.retrievedDocuments && metadata.retrievedDocuments.length > 0 && (
          <div className="mb-4 px-2">
            <details className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <summary className="text-sm text-blue-800 cursor-pointer font-medium">
                📚 Referenced {metadata.documentsRetrieved} file{metadata.documentsRetrieved !== 1 ? 's' : ''} 
                {metadata.processingTime && ` (${metadata.processingTime.toFixed(1)}s)`}
              </summary>
              <ul className="mt-2 space-y-1 text-xs text-blue-700">
                {metadata.retrievedDocuments.map((doc, i) => (
                  <li key={i} className="flex justify-between">
                    <span className="truncate flex-1">{doc.filePath}</span>
                    <span className="ml-2 text-blue-600">
                      {(doc.similarity * 100).toFixed(0)}% match
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mb-4 px-2">
            <div className="bg-red-100 border border-red-300 text-red-800 px-4 py-2 rounded-lg text-sm">
              {error}
            </div>
          </div>
        )}

        {/* Code Analysis Form */}
        {showAnalysisForm && (
          <div>
            <CodeAnalysisForm
              projectId={projectId}
              onSubmit={handleCodeAnalysis}
              onCancel={() => setShowAnalysisForm(false)}
              isAnalyzing={isAnalyzing}
            />
          </div>
        )}
        
        {/* Input Area - Hidden when analysis form is shown */}
        {!showAnalysisForm && (
          <div className="space-y-3">
            {/* AI Disclaimer */}
            <div className="px-2">
              <p className="text-xs text-gray-500 text-center italic">
                ⚠️ Responses are AI-generated and may contain errors. Use as reference only.
              </p>
            </div>
            
            {/* Process Selector - Only show when there are multiple processes */}
            {processes.length > 1 && (
              <div className="px-2">
                <ProcessSelector
                  processes={processes}
                  selectedProcessId={selectedProcessId}
                  onProcessChange={handleProcessChange}
                  isLoading={isLoadingProcesses}
                />
              </div>
            )}
            
            {/* Input Box */}
            <div className="flex space-x-2">
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={selectedProcessId 
                  ? `Ask about this process's workflows, activities, or structure... (Ctrl+Enter for new line)` 
                  : `Ask about workflows, activities, or project structure... (Ctrl+Enter for new line)`}
                className="flex-grow rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none min-h-[42px] max-h-[200px]"
                disabled={isTyping || isAnalyzing}
                rows={1}
                style={{
                  height: 'auto',
                  overflow: 'auto'
                }}
                onInput={(e) => {
                  // Auto-resize textarea based on content
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
                }}
              />
              <button
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isTyping || isAnalyzing}
                className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed self-end"
              >
                {isTyping ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
      
      {/* Chat History Sidebar - Fixed width */}
      <div className="w-80 h-full">
        <ChatHistorySidebar
          conversations={conversations}
          currentConversationId={currentConversationId}
          onSelectConversation={loadConversation}
          onNewChat={handleNewChat}
          onDeleteConversation={handleDeleteConversation}
          onAnalyzeCode={handleAnalyzeCodeClick}
          isLoading={isLoadingConversations}
          availableModels={availableModels}
          currentModel={currentModel}
          onModelChange={handleModelChange}
        />
      </div>

      {/* Google Docs Export Success Modal */}
      <GoogleDocsExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        documentUrl={exportedDocument.url}
        title={exportedDocument.title}
      />

      {/* Clear History Confirmation Modal */}
      {showClearHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-96">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete All Conversations</h3>
            <p className="text-sm text-gray-700 mb-6">
              Are you sure you want to delete all conversations? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancelClearHistory}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmClearHistory}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Delete All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Google Docs Permission Request Modal */}
      {showGoogleDocsPermissionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-96">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Google Docs Permission Required</h3>
            <p className="text-sm text-gray-700 mb-6">
              Google Docs permissions are required to export chat responses.
              <br /><br />
              Click <strong>Grant Permission</strong> to allow this app to create and access Google Docs on your behalf.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancelGoogleDocsPermission}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmGoogleDocsPermission}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Grant Permission
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatTab;